import { useEffect, useRef } from 'react';
import { useSucursalesUsuarioContext } from '../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from './useSucursalesVentaFiltro';
import { resolveSucursalPreferidaNro } from '../utils/sucursalPreferida';

export interface UseAutoSucursalPredeterminadaOptions {
  nroSuc?: number | null;
  onApply: (nroSuc: number, nombreSucursal: string) => void;
  /** Si es false, no se aplica */
  enabled?: boolean;
  /** Cambia en cada navegación a un comprobante nuevo (p. ej. location.key). */
  resetKey?: string | number | null;
}

/**
 * En comprobantes nuevos aplica siempre la sucursal marcada con estrella al abrir.
 * La elección manual vale solo mientras el formulario está abierto.
 */
export function useAutoSucursalPredeterminada({
  nroSuc,
  onApply,
  enabled = true,
  resetKey,
}: UseAutoSucursalPredeterminadaOptions): void {
  const { preferidaNro, preferidaSucursal, sucursales, loading } = useSucursalesUsuarioContext();
  const omitirPorEdicionManualRef = useRef(false);
  const ultimaPreferidaAplicadaRef = useRef<number | null>(null);

  useEffect(() => {
    omitirPorEdicionManualRef.current = false;
    ultimaPreferidaAplicadaRef.current = null;
  }, [enabled, resetKey]);

  useEffect(() => {
    if (!enabled || loading) return;

    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida == null) return;

    const current = Number(nroSuc ?? 0);

    if (
      ultimaPreferidaAplicadaRef.current === preferida &&
      current > 0 &&
      current !== preferida
    ) {
      omitirPorEdicionManualRef.current = true;
      return;
    }

    if (omitirPorEdicionManualRef.current) return;
    if (current === preferida) {
      ultimaPreferidaAplicadaRef.current = preferida;
      return;
    }

    ultimaPreferidaAplicadaRef.current = preferida;
    const nombre =
      (preferidaSucursal?.nombreSucursal ?? '').trim() ||
      sucursales.find((s) => Number(s.nroSuc) === preferida)?.nombreSucursal?.trim() ||
      `Sucursal ${preferida}`;
    onApply(preferida, nombre);
    persistGlobalNroSuc(preferida);
  }, [enabled, loading, nroSuc, preferidaNro, preferidaSucursal, sucursales, onApply]);
}
